import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getRiskReadingList(
    $filter: readingFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getReadingList(filter: $filter, pagination: $pagination, sort: $sort) {
      items {
        ishReading {
          score
          color
          referenceRange
        }
        weighingReading {
          weightValue
          bmiFlag
          bmiColor
          bmiValue
          bmiReferenceRange
        }
        glucometerReading {
          randomFlag
          randomColor
          randomValue
          randomReferenceRange
          fastingFlag
          fastingColor
          fastingValue
          fastingReferenceRange
          ppFlag
          ppColor
          ppValue
          ppReferenceRange
        }
        bpReading {
          systolicFlag
          systolicColor
          systolicValue
          systolicReferenceRange
          diastolicFlag
          diastolicColor
          diastolicValue
          diastolicReferenceRange
          meanFlag
          meanColor
          meanValue
        }
        vincenseReading {
          pulseFlag
          pulseColor
          pulseValue
          pulseReferenceRange
          o2Flag
          o2Color
          o2Value
          o2ReferenceRange
          rrFlag
          rrColor
          rrValue
          rrReferenceRange
          skinValue
        }
        datetime
        id
      }
      pagination {
        totalCount
        pageLimit
        pageNumber
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.ReadingListState = {
  result: null,
  error: null,
  loading: false
};

export const getRiskReadingListRequest = createAsyncThunk<
  TYPES.ReadingList,
  {
    filter?: TYPES.ReadingFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'reading/get_risk_reading_list',
  async (
  params: {
    filter?: TYPES.ReadingFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getReadingList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getRiskReadingListSlice = createSlice({
  name: 'get_risk_reading_list',
  initialState: initialState,
  reducers: {
    getRiskReadingListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getRiskReadingListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getRiskReadingListRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getRiskReadingListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getRiskReadingListClear } =
getRiskReadingListSlice.actions;

export const getRiskReadingList = createSelector(
  (state: RootState) => state.reading.getRiskReadingList.result,
  (state: RootState) => state.reading.getRiskReadingList.error,
  (state: RootState) => state.reading.getRiskReadingList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getRiskReadingListSlice.reducer;